import React, { useState, useEffect } from 'react';
import { FiDownload, FiCalendar, FiTruck, FiMapPin, FiPieChart } from 'react-icons/fi';
import { Chart, registerables } from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Register Chart.js components
Chart.register(...registerables);

const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('usage');
  const [dateRange, setDateRange] = useState('week');
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch report data
    const fetchReportData = async () => {
      try {
        setLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Generate dummy data based on report type and date range
        const data = generateDummyReportData(reportType, dateRange);
        
        setReportData(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load report data');
        setLoading(false);
        console.error('Error fetching report data:', err);
      }
    };

    fetchReportData();
  }, [reportType, dateRange]);

  // Generate dummy data for different report types
  const generateDummyReportData = (type, range) => {
    const daysInRange = range === 'day' ? 1 : range === 'week' ? 7 : range === 'month' ? 30 : 90;
    
    // Generate date labels
    const today = new Date();
    const labels = Array(daysInRange).fill().map((_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (daysInRange - 1 - i));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    // Usage report data
    if (type === 'usage') {
      return {
        title: 'Vehicle Usage Report',
        description: `Shows vehicle activity over the last ${range}`,
        charts: [
          {
            id: 'distanceChart',
            title: 'Distance Traveled (miles)',
            type: 'line',
            data: {
              labels,
              datasets: [
                {
                  label: 'Fleet Total',
                  data: labels.map(() => Math.floor(Math.random() * 500) + 300),
                  borderColor: 'rgb(53, 162, 235)',
                  backgroundColor: 'rgba(53, 162, 235, 0.5)',
                  tension: 0.3
                },
                {
                  label: 'Average Per Vehicle',
                  data: labels.map(() => Math.floor(Math.random() * 100) + 50),
                  borderColor: 'rgb(75, 192, 192)',
                  backgroundColor: 'rgba(75, 192, 192, 0.5)',
                  tension: 0.3
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Miles'
                  }
                }
              }
            }
          },
          {
            id: 'activeVehiclesChart',
            title: 'Active Vehicles',
            type: 'bar',
            data: {
              labels,
              datasets: [
                {
                  label: 'Number of Active Vehicles',
                  data: labels.map(() => Math.floor(Math.random() * 5) + 10),
                  backgroundColor: 'rgba(255, 99, 132, 0.5)',
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Vehicles'
                  }
                }
              }
            }
          }
        ],
        stats: [
          { label: 'Total Distance', value: '2,345 miles', change: '+12%' },
          { label: 'Active Vehicles', value: '15', change: '+2' },
          { label: 'Average Speed', value: '42 mph', change: '-3%' },
          { label: 'Idle Time', value: '32 hours', change: '+5%' }
        ]
      };
    }
    
    // Geofence report data
    if (type === 'geofence') {
      return {
        title: 'Geofence Activity Report',
        description: `Shows geofence entries and exits over the last ${range}`,
        charts: [
          {
            id: 'geofenceActivityChart',
            title: 'Geofence Activity',
            type: 'bar',
            data: {
              labels,
              datasets: [
                {
                  label: 'Entries',
                  data: labels.map(() => Math.floor(Math.random() * 10) + 5),
                  backgroundColor: 'rgba(75, 192, 192, 0.5)',
                },
                {
                  label: 'Exits',
                  data: labels.map(() => Math.floor(Math.random() * 10) + 5),
                  backgroundColor: 'rgba(153, 102, 255, 0.5)',
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Count'
                  }
                }
              }
            }
          },
          {
            id: 'geofenceDistributionChart',
            title: 'Geofence Distribution',
            type: 'pie',
            data: {
              labels: ['Downtown Zone', 'Warehouse Area', 'Customer Sites', 'Restricted Areas', 'Other'],
              datasets: [
                {
                  data: [25, 30, 15, 10, 20],
                  backgroundColor: [
                    'rgba(255, 99, 132, 0.5)',
                    'rgba(54, 162, 235, 0.5)',
                    'rgba(255, 206, 86, 0.5)',
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(153, 102, 255, 0.5)',
                  ],
                  borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)',
                  ],
                  borderWidth: 1,
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  position: 'right',
                },
              }
            }
          }
        ],
        stats: [
          { label: 'Total Entries', value: '152', change: '+8%' },
          { label: 'Total Exits', value: '147', change: '+7%' },
          { label: 'Most Active Zone', value: 'Warehouse', change: '' },
          { label: 'Geofence Alerts', value: '12', change: '-25%' }
        ]
      };
    }
    
    // Alert report data
    if (type === 'alerts') {
      return {
        title: 'Alert Analysis Report',
        description: `Shows alerts and incidents over the last ${range}`,
        charts: [
          {
            id: 'alertsBySeverityChart',
            title: 'Alerts by Severity',
            type: 'pie',
            data: {
              labels: ['Low', 'Medium', 'High', 'Critical'],
              datasets: [
                {
                  data: [45, 30, 20, 5],
                  backgroundColor: [
                    'rgba(75, 192, 192, 0.5)',
                    'rgba(255, 206, 86, 0.5)',
                    'rgba(255, 159, 64, 0.5)',
                    'rgba(255, 99, 132, 0.5)',
                  ],
                  borderColor: [
                    'rgba(75, 192, 192, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(255, 159, 64, 1)',
                    'rgba(255, 99, 132, 1)',
                  ],
                  borderWidth: 1,
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  position: 'right',
                },
              }
            }
          },
          {
            id: 'alertsOverTimeChart',
            title: 'Alerts Over Time',
            type: 'line',
            data: {
              labels,
              datasets: [
                {
                  label: 'Total Alerts',
                  data: labels.map(() => Math.floor(Math.random() * 10) + 5),
                  borderColor: 'rgb(255, 99, 132)',
                  backgroundColor: 'rgba(255, 99, 132, 0.5)',
                  tension: 0.3
                },
                {
                  label: 'Acknowledged',
                  data: labels.map(() => Math.floor(Math.random() * 8) + 3),
                  borderColor: 'rgb(75, 192, 192)',
                  backgroundColor: 'rgba(75, 192, 192, 0.5)',
                  tension: 0.3
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
                tooltip: {
                  mode: 'index',
                  intersect: false,
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Alerts'
                  }
                }
              }
            }
          },
          {
            id: 'alertTypeDistributionChart',
            title: 'Alert Type Distribution',
            type: 'bar',
            data: {
              labels: ['Geofence', 'Speeding', 'Idle Time', 'Offline', 'Low Fuel', 'Maintenance'],
              datasets: [
                {
                  label: 'Count',
                  data: [28, 35, 15, 8, 12, 7],
                  backgroundColor: 'rgba(54, 162, 235, 0.5)',
                }
              ]
            },
            options: {
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Count'
                  }
                }
              }
            }
          }
        ],
        stats: [
          { label: 'Total Alerts', value: '105', change: '-5%' },
          { label: 'Acknowledged', value: '98', change: '+12%' },
          { label: 'Response Time', value: '12 min', change: '-15%' },
          { label: 'Critical Alerts', value: '5', change: '-40%' }
        ]
      };
    }
    
    return null;
  };

  // Render chart based on type
  const renderChart = (chart) => {
    switch (chart.type) {
      case 'line':
        return <Line data={chart.data} options={chart.options} />;
      case 'bar':
        return <Bar data={chart.data} options={chart.options} />;
      case 'pie':
        return <Pie data={chart.data} options={chart.options} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <button 
          className="mt-4 btn btn-primary" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Fleet analytics and reporting</p>
        </div>
        <button className="bg-primary text-white px-4 py-2 rounded-lg flex items-center">
          <FiDownload className="mr-2" />
          Export Report
        </button>
      </div>

      {/* Report controls */}
      <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
          <div className="flex space-x-2">
            <button
              onClick={() => setReportType('usage')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                reportType === 'usage' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiTruck className="mr-2" />
              Usage
            </button>
            <button
              onClick={() => setReportType('geofence')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                reportType === 'geofence' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiMapPin className="mr-2" />
              Geofence
            </button>
            <button
              onClick={() => setReportType('alerts')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                reportType === 'alerts' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiPieChart className="mr-2" />
              Alerts
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
          <div className="flex space-x-2">
            <button
              onClick={() => setDateRange('day')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                dateRange === 'day' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiCalendar className="mr-2" />
              Day
            </button>
            <button
              onClick={() => setDateRange('week')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                dateRange === 'week' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiCalendar className="mr-2" />
              Week
            </button>
            <button
              onClick={() => setDateRange('month')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                dateRange === 'month' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiCalendar className="mr-2" />
              Month
            </button>
            <button
              onClick={() => setDateRange('quarter')}
              className={`flex items-center px-3 py-2 rounded-lg ${
                dateRange === 'quarter' ? 
                'bg-primary text-white' : 
                'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiCalendar className="mr-2" />
              Quarter
            </button>
          </div>
        </div>
      </div>

      {/* Report content */}
      {reportData && (
        <div className="space-y-6">
          {/* Report header */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900">{reportData.title}</h2>
            <p className="text-gray-600">{reportData.description}</p>
            
            {/* Stats summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              {reportData.stats.map((stat, i) => (
                <div key={i} className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-gray-500 text-sm">{stat.label}</div>
                  <div className="flex items-end justify-between mt-1">
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    {stat.change && (
                      <div className={`text-sm font-medium ${
                        stat.change.startsWith('+') ? 'text-ui-success' : 
                        stat.change.startsWith('-') ? 'text-ui-error' : 
                        'text-gray-500'
                      }`}>
                        {stat.change}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {reportData.charts.map((chart) => (
              <div key={chart.id} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{chart.title}</h3>
                {renderChart(chart)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;